/* eslint-disable max-lines */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { RecoilRoot } from 'recoil';
import _, { cloneDeep } from 'lodash';
import { Portal } from '@rexlabs/portal';
import { styled, StyleSheet } from '@rexlabs/styling';
import { api } from 'src/api-client';
import { token } from 'src/twig-react-old';

import Popup from '../popups/Popup';
import { Checkbox } from '../checkbox';
import SlideoutContainer from '../slide-out/slideout-container';
import { Loader } from '../loader';

import SlideoutFormRenderer from './slideout-form-renderer';
import FormRenderer from './form-renderer';

const defaultStyles = StyleSheet({
  text: {
    color: '#333'
  },
  required: {
    color: token('color.danger.idle.default')
  },
  loadingContainer: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
});

@autobind
class Form extends PureComponent {
  state = {
    submitting: false,
    submitted: false,
    loading: true,
    successMessage: "Thanks, we'll be in touch soon.",
    errorMessage: '',
    form: {},
    popup: null
  };

  loadForm(url) {
    api
      .get(url)
      .then((response) => {
        // Error handling
        if (!response?.data || response.status !== 200) {
          this.setState({
            loading: false,
            errorMessage:
              'Error: Form failed to load. It may be configured incorrectly. If you are the owner of this site, please contact support'
          });
          return;
        }
        this.convertFormForRender(response?.data);
      })
      .catch((error) => {
        this.setState({
          loading: false,
          errorMessage: error
        });
        console.error('error', error);
      });
  }

  componentWillMount() {
    this.reloadForm();
  }

  reloadForm() {
    const { websiteId, formLocation, formId, form } = this.props;
    const wid = websiteId || form?.website_id;
    if (form) {
      this.convertFormForRender(form);
    } else if (formId) {
      this.loadForm(`/websites/${wid}/forms/${formId}?include=popups.triggers`);
    } else if (formLocation) {
      this.loadForm(
        `/websites/${wid}/forms/location/${formLocation}?include=popups.triggers`
      );
    } else {
      this.setState({ loading: false, form: null });
    }
  }

  convertFormForRender(form) {
    const convertedForm = cloneDeep(form);

    // TEMP hack/fix since form renderer currently doesn't
    // support schemas with no page defined
    convertedForm.definition.schema = '2_draft';
    convertedForm.definition.pages = [
      {
        name: 'default',
        label: 'default'
      }
    ];
    convertedForm.definition.groups = [];

    convertedForm.definition.fields = convertedForm.definition.fields.map(
      (field) => {
        return this.convertFieldData(field);
      }
    );

    this.setState({
      loading: false,
      form: convertedForm
    });
  }

  handleActions(actions) {
    actions.data.forEach((item) => {
      if (item.type.id === 'redirect') {
        window.location.href = item.redirect_url;
      } else if (item.type.id === 'success') {
        this.setState({
          successMessage: item.success_message
        });
      }
    });
  }

  handleSubmit(values) {
    const {
      websiteId,
      formLocation,
      resourceId,
      resourceType,
      formExtraFields = {}
    } = this.props;
    const { form } = this.state;

    this.setState({
      submitting: true,
      submitted: false
    });

    const wid = websiteId || form?.website_id;

    return api
      .post(`/websites/${wid}/submissions`, {
        resource_id: resourceId,
        resource_type: resourceType,
        form_location: formLocation,
        form_id: form.id,
        data: {
          ...values,
          ...formExtraFields,
          form_name: form?.definition?.label
        }
      })
      .then((response) => {
        this.setState({
          submitting: false
        });

        if (response.status === 201) {
          this.setState({
            submitted: true
          });

          // Get popups
          const popup = form?.popups?.data?.[0];
          if (popup) {
            this.setState({ popup: popup });
          }

          if (response?.data?.actions) {
            this.handleActions(response?.data?.actions);
          }
        } else {
          this.setState({
            errorMessage:
              'Error: Unexpected response from server. The form could not be submitted.'
          });
        }
      })
      .catch((error) => {
        this.setState({
          submitting: false,
          errorMessage: error
        });

        console.error(error);
      });
  }

  closePopup() {
    this.setState({ popup: null });
  }

  convertFieldData(field) {
    // Check we actually have field data
    if (!field?.key) {
      this.setState({
        errorMessage: "Error: The form couldn't be shown."
      });
      return;
    }

    const convertedField = {};
    convertedField.description = field?.description;
    convertedField.placeholder = field?.placeholder;
    convertedField.fieldProps = {};
    convertedField.fieldProps.inputProps = {};
    convertedField.name = field?.key;
    convertedField.label = field?.label;
    convertedField.page = 'default';

    if (field?.validate?.required) {
      const { styles: s } = this.props;
      convertedField.label = (
        <span>
          {convertedField.label} <span {...s('required')}>*</span>
        </span>
      );
      convertedField.validation = ['required'];
    }

    /** The input types we store in the database don't match luna's **/
    const inputMap = {
      textfield: 'text',
      textarea: 'textarea',
      radio: 'radio-group',
      checkbox: 'checkbox',
      selectboxes: 'checkbox', // TODO loop through and convert to single checkboxes
      select: 'select',
      dateTime: 'datetime',
      day: 'date',
      time: 'time'
    };

    if (field?.type === 'select' && field?.key === 'multiSelect') {
      convertedField.type = 'multi-select';
    } else {
      convertedField.type = inputMap[field?.type] || field?.type;
    }

    // HACK: Form renderer doesn't support multiple checkboxes. Multi select is an equivalent
    if (field?.type === 'selectboxes') {
      convertedField.type = 'multi-select';
    }

    // Single checkboxes only have the label next to it, from input props
    if (field?.type === 'checkbox') {
      convertedField.fieldProps.inputProps.label = field?.label;
      convertedField.label = undefined;
    }

    // Select fields get their data from data.values instead of just values
    if (field?.type === 'select') {
      convertedField.data = field?.data?.values;

      // Ensure data is an array, just in case
      if (!Array.isArray(convertedField.data)) {
        convertedField.data = [];
      }
    } else {
      convertedField.data = field?.values;
    }

    return convertedField;
  }

  render() {
    const { styles: s, textStyles, slideout, onClose } = this.props;
    const {
      submitting,
      submitted,
      successMessage,
      form,
      popup,
      errorMessage
    } = this.state;

    if (!_.isEmpty(form)) {
      return (
        <>
          <RecoilRoot>
            {(!submitted && (
              <>
                {slideout ? (
                  <SlideoutFormRenderer
                    submitting={submitting}
                    schema={form}
                    handleSubmit={this.handleSubmit}
                    CheckboxInput={Checkbox}
                    onClose={onClose}
                  />
                ) : (
                  <FormRenderer
                    submitting={submitting}
                    schema={form}
                    handleSubmit={this.handleSubmit}
                    CheckboxInput={Checkbox}
                  />
                )}
              </>
            )) || (
              <SlideoutContainer>
                <div
                  dangerouslySetInnerHTML={{ __html: successMessage }}
                  {...s.with('text')(textStyles)}
                />
              </SlideoutContainer>
            )}
            {!submitted && !submitting && errorMessage && (
              <SlideoutContainer>
                <p className='form-error'>{errorMessage}</p>
              </SlideoutContainer>
            )}
            {popup && (
              <Portal target={'popups'}>
                <Popup popup={popup} closeModal={this.closePopup} />
              </Portal>
            )}
          </RecoilRoot>
        </>
      );
    } else if (errorMessage) {
      return <p {...s.with('text')(textStyles)}>{errorMessage}</p>;
    } else {
      return (
        <p
          className='font-size-normal weight-medium'
          {...s.with('text')(textStyles)}
        >
          <SlideoutContainer {...s('loadingContainer')}>
            <Loader />
          </SlideoutContainer>
        </p>
      );
    }
  }
}

Form.propTypes = {
  form: PropTypes.object,
  websiteId: PropTypes.string,
  formLocation: PropTypes.string,
  successStyles: PropTypes.object,
  formExtraFields: PropTypes.object
};

const StyledForm = styled(defaultStyles)(Form);

export default StyledForm;
window.__Components.Form = StyledForm;
